import { useState, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, RefreshCcw, AlertTriangle, Database } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";

interface PostgresError {
  id: string;
  event_message: string;
  error_severity: string;
  timestamp: number; // Unix timestamp in microseconds
  identifier: string; // Project ID
}

export const PostgresErrorViewer = () => {
  const [errors, setErrors] = useState<PostgresError[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("optimized_images");
  
  const fetchPostgresErrors = async () => {
    try {
      setLoading(true);
      console.log("Fetching Postgres errors...");
      
      // Get admin settings to check if we're running in testing mode
      const { data: settingsData } = await supabase
        .from("admin_settings")
        .select("testing_mode")
        .eq("id", 1)
        .single();
      
      const isTestingMode = settingsData?.testing_mode;
      
      // In a real app, we'd fetch from analytics logs API
      // Since we can't do that in the client directly, we'll use stored errors
      // This simulates what would happen in a real backend call
      
      // We can capture some data through regular API calls for diagnostics
      try {
        // Test that the optimized_images table exists
        const { error } = await supabase
          .from("optimized_images")
          .select("id")
          .limit(1);
          
        if (error) {
          console.log("Diagnostic query error:", error);
        }
      } catch (queryError) {
        console.error("Expected diagnostic error:", queryError);
      }
      
      // Read from the console log captured in the DiagnosticsTab
      // and filter out relevant Postgres errors
      const logData = localStorage.getItem("captured_console_logs");
      let mockErrors: PostgresError[] = [];
      
      if (logData) {
        const logs = JSON.parse(logData);
        mockErrors = logs
          .filter((log: string) => {
            // Parse database errors from the logs
            return log.includes("ERROR") && 
                  (filter ? log.toLowerCase().includes(filter.toLowerCase()) : true) &&
                  (log.includes("relation") || log.includes("database") || log.includes("query"))
          })
          .map((log: string, index: number) => {
            // Extract a timestamp if available
            const timestampMatch = log.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);
            const timestamp = timestampMatch 
              ? new Date(timestampMatch[0]).getTime() * 1000 
              : Date.now() * 1000;
              
            // Try to determine severity
            let severity = "ERROR";
            if (log.includes("[WARN]")) severity = "WARNING";
            if (log.includes("[ERROR]")) severity = "ERROR";
            
            return {
              id: `err-${index}-${Date.now()}`,
              event_message: log,
              error_severity: severity,
              timestamp,
              identifier: "ginefbvgxcobyuocxerb"
            };
          });
      }
      
      // Add some sample errors for development guidance
      if (isTestingMode && mockErrors.length === 0) {
        mockErrors = [
          {
            id: "sample-1",
            event_message: 'relation "public.optimized_images" does not exist',
            error_severity: "ERROR",
            timestamp: Date.now() * 1000,
            identifier: "ginefbvgxcobyuocxerb"
          },
          {
            id: "sample-2",
            event_message: 'relation "public.optimized_images" does not exist in line 143',
            error_severity: "ERROR", 
            timestamp: (Date.now() - 60000) * 1000,
            identifier: "ginefbvgxcobyuocxerb"
          }
        ];
      }
      
      setErrors(mockErrors);
    } catch (error) {
      console.error("Error fetching Postgres errors:", error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPostgresErrors();
    // Fetch errors when the component mounts
    const intervalId = setInterval(fetchPostgresErrors, 60000); // Refresh every minute
    
    return () => clearInterval(intervalId);
  }, [filter]);
  
  const formatTimestamp = (timestamp: number) => {
    // Convert from microseconds to milliseconds
    const date = new Date(timestamp / 1000); 
    return date.toLocaleString();
  };
  
  const getSeverityColor = (severity: string) => {
    switch (severity.toUpperCase()) {
      case "ERROR": return "destructive";
      case "WARNING": return "secondary"; // Using "secondary" since "warning" isn't available
      case "LOG": return "secondary";
      default: return "secondary";
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            <span>Postgres Error Log</span>
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchPostgresErrors}
            disabled={loading}
          >
            <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Loading..." : "Refresh"}
          </Button>
        </div>
        <div className="flex gap-2 items-center">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter errors (e.g., optimized_images)"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-sm"
          />
        </div>
      </CardHeader>
      <CardContent>
        {errors.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <AlertTriangle className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
            <p>No database errors found matching your filter criteria.</p>
            <p className="text-sm mt-2">Try changing the filter or refreshing the logs.</p>
          </div>
        ) : (
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {errors.map((error) => (
                <div
                  key={error.id}
                  className="p-3 border rounded-md bg-muted/50"
                >
                  <div className="flex justify-between items-start">
                    <Badge variant={getSeverityColor(error.error_severity)}>
                      {error.error_severity}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(error.timestamp)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-mono break-all whitespace-pre-wrap">
                    {error.event_message}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
        
        <div className="mt-4 p-3 border rounded-md bg-amber-50 dark:bg-amber-950">
          <h4 className="font-medium flex items-center gap-1 text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" />
            Database Table Missing
          </h4>
          <p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
            It appears the "optimized_images" table is missing from the database. 
            This table is used for tracking image optimization status.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2 bg-amber-100 hover:bg-amber-200 dark:bg-amber-900 dark:hover:bg-amber-800"
            onClick={() => {
              // In a real app, this would trigger table creation
              console.log("Fix table requested");
            }}
          >
            Create Missing Table
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PostgresErrorViewer;
